import { useTranslation } from 'react-i18next';
import React from 'react';
import BoldVerticalBarChart, {
	BoldVerticalBarChartProps,
} from '@components/report/chart/bold-vertical-bar-chart/BoldVerticalBarChart';
import LineChart, { LineChartProps } from '@components/report/chart/line-chart/LineChart';
import RadialBarChart, {
	RadialBarChartProps,
} from '@components/report/chart/radial-bar-chart/RadialBarChart';
import TriangleChart, {
	TriangleChartProps,
} from '@components/report/chart/triangle-chart/TriangleChart';
import VerticalBarAverageChart, {
	VerticalBarAverageChartProps,
} from '@components/report/chart/vertical-bar-average-chart/VerticalBarAverageChart';
import ReportCard from '@components/report/report-card/ReportCard';
import { threeDivisionGridClass } from '@components/report/style.css';

export interface FollowerGrowthIgProps {
	activeFollowersRate?: RadialBarChartProps;
	activeFollowersComparison?: BoldVerticalBarChartProps;
	averageReach?: VerticalBarAverageChartProps;
	followerTrend?: LineChartProps;
	followerGrowthRate?: TriangleChartProps;
	followingTrend?: LineChartProps;

	isLoading?: boolean;
	isError?: boolean;
	lackOfPermission?: boolean;
}

const FollowerGrowthIg = ({
	activeFollowersRate,
	activeFollowersComparison,
	averageReach,
	followerTrend,
	followerGrowthRate,
	followingTrend,

	isLoading,
	isError,
	lackOfPermission,
}: FollowerGrowthIgProps) => {
	const status = { isLoading, isError, lackOfPermission };
	const { t } = useTranslation('infReportInstagram');

	return (
		<div className={threeDivisionGridClass}>
			<ReportCard
				label={t(
					'features.report.section-content.ig.follower-growth.active-followers-rate.label',
				)}
				tooltip={t(
					'features.report.section-content.ig.follower-growth.active-followers-rate.tooltip',
				)}
				lackOfPermission={lackOfPermission}
			>
				<RadialBarChart
					title={t(
						'features.report.section-content.ig.follower-growth.active-followers-rate.radial-bar-chart.title',
					)}
					legends={[
						{
							color: 'primary',
							label: t(
								'features.report.section-content.ig.follower-growth.active-followers-rate.radial-bar-chart.active-followers',
							),
							isMain: true,
						},
						{
							color: 'gray',
							label: t(
								'features.report.section-content.ig.follower-growth.active-followers-rate.radial-bar-chart.followers',
							),
						},
					]}
					{...status}
					{...activeFollowersRate}
				/>
			</ReportCard>

			<ReportCard
				label={t(
					'features.report.section-content.ig.follower-growth.active-followers-comparison.label',
				)}
				tooltip={t(
					'features.report.section-content.ig.follower-growth.active-followers-comparison.tooltip',
				)}
				lackOfPermission={lackOfPermission}
			>
				<BoldVerticalBarChart
					desc={true}
					data={[{ isMain: true }, {}]}
					{...status}
					{...activeFollowersComparison}
				/>
			</ReportCard>

			<ReportCard
				label={t(
					'features.report.section-content.ig.follower-growth.average-reach.label',
				)}
				tooltip={t(
					'features.report.section-content.ig.follower-growth.average-reach.tooltip',
				)}
				lackOfPermission={lackOfPermission}
			>
				<VerticalBarAverageChart
					subDesc={t(
						'features.report.section-content.ig.follower-growth.average-reach.vertical-bar-average-chart.sub-description',
					)}
					valueUnit={t(
						'features.report.section-content.ig.follower-growth.average-reach.vertical-bar-average-chart.value-unit',
					)}
					label={t(
						'features.report.section-content.ig.follower-growth.average-reach.vertical-bar-average-chart.label',
					)}
					desc
					{...averageReach}
					{...status}
				/>
			</ReportCard>
			<ReportCard
				label={t(
					'features.report.section-content.ig.follower-growth.follower-trend.label',
				)}
				tooltip={t(
					'features.report.section-content.ig.follower-growth.follower-trend.tooltip',
				)}
				lackOfPermission={lackOfPermission}
			>
				<LineChart
					subDesc={t(
						'features.report.section-content.ig.follower-growth.follower-trend.line-chart.sub-description',
					)}
					data_name_a={t(
						'features.report.section-content.ig.follower-growth.follower-trend.line-chart.follower',
					)}
					desc
					{...status}
					{...followerTrend}
				/>
			</ReportCard>
			<ReportCard
				label={t(
					'features.report.section-content.ig.follower-growth.follower-growth-rate.label',
				)}
				tooltip={t(
					'features.report.section-content.ig.follower-growth.follower-growth-rate.tooltip',
				)}
				lackOfPermission={lackOfPermission}
			>
				<TriangleChart desc {...status} {...followerGrowthRate} />
			</ReportCard>
			<ReportCard
				label={t(
					'features.report.section-content.ig.follower-growth.following-trend.label',
				)}
				tooltip={t(
					'features.report.section-content.ig.follower-growth.following-trend.tooltip',
				)}
				lackOfPermission={lackOfPermission}
			>
				<LineChart
					subDesc={t(
						'features.report.section-content.ig.follower-growth.following-trend.line-chart.sub-description',
					)}
					data_name_a={t(
						'features.report.section-content.ig.follower-growth.following-trend.line-chart.following',
					)}
					desc={true}
					{...status}
					{...followingTrend}
				/>
			</ReportCard>
		</div>
	);
};

export default FollowerGrowthIg;
